import React, { useContext, useMemo } from 'react';
import { Grid, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { PrimaryButton } from '@/styles/blocks';
import { Context } from '../store';
import { formatItemIdentifier, formatItemDetail, groupAddedItemsByOrder, isWholeOrderAdded } from '../utils';

const AddedOrderCard = ({ order, items, addedItems, callbacks }) => {
    const isFullOrder = isWholeOrderAdded(order, addedItems);
    const showItems = items.length > 1 || !isFullOrder;

    return (
        <Grid
            container
            direction="column"
            css={css`
                border: 1px solid ${colors.greys.border};
                border-radius: 4px;
                padding: 0.5rem 0.75rem;
                flex-wrap: nowrap;
            `}
        >
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-start" wrap="nowrap">
                <Grid
                    container
                    direction="column"
                    css={css`
                        min-width: 0;
                    `}
                >
                    <span
                        css={css`
                            font-weight: 700;
                            font-size: 14px;
                        `}
                    >
                        {order?.order_number || 'Order'}
                    </span>
                    {/* Same field the left-hand orders table shows as "Customer" */}
                    <span
                        css={css`
                            font-size: 13px;
                            color: ${colors.greys.secondary};
                        `}
                    >
                        {order?.dropoff_name || '-'}
                    </span>
                </Grid>
                <IconButton size="small" onClick={() => callbacks.removeManifestItems(items.map((i) => i.item_id))}>
                    <Close fontSize="small" />
                </IconButton>
            </Grid>
            {showItems
                ? items.map((item) => (
                      <Grid
                          key={item.item_id}
                          container
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          css={css`
                              padding: 0.25rem 0 0.25rem 0.75rem;
                              flex-wrap: nowrap;
                              border-top: 1px solid ${colors.greys.border};
                          `}
                      >
                          <Grid
                              container
                              direction="column"
                              css={css`
                                  min-width: 0;
                              `}
                          >
                              <span
                                  css={css`
                                      font-size: 13px;
                                      font-weight: 500;
                                  `}
                              >
                                  {formatItemIdentifier(item)}
                              </span>
                              <span
                                  css={css`
                                      font-size: 13px;
                                      color: ${colors.greys.secondary};
                                  `}
                              >
                                  {formatItemDetail(item)}
                              </span>
                          </Grid>
                          <IconButton size="small" onClick={() => callbacks.removeManifestItems([item.item_id])}>
                              <Close fontSize="small" />
                          </IconButton>
                      </Grid>
                  ))
                : null}
        </Grid>
    );
};

const ManifestColumn = ({ onCreate, createDisabled, onDeleteDraft, isUpdate = false }) => {
    const { state, callbacks } = useContext(Context);
    const addedItems = state.newManifest?.items;

    const addedOrders = useMemo(
        () => groupAddedItemsByOrder(addedItems, state.itemsMap, state.itemQuantities),
        [addedItems, state.itemsMap, state.itemQuantities]
    );

    return (
        <Grid
            container
            direction="column"
            css={css`
                flex: 4 1 0;
                /* Long order/SKU strings would otherwise set a floor this can't shrink past. */
                min-width: 0;
                flex-wrap: nowrap;
                border-left: 1px solid ${colors.greys.border};
                padding-left: 1rem;
            `}
        >
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                css={css`
                    margin-bottom: 0.5rem;
                `}
            >
                <span
                    css={css`
                        font-weight: 700;
                        font-size: 16px;
                    `}
                >
                    Manifest
                </span>
                <span
                    css={css`
                        color: ${colors.greys.secondary};
                        font-size: 14px;
                    `}
                >
                    Orders: {addedOrders.length}
                </span>
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex-grow: 1;
                    flex-wrap: nowrap;
                    overflow-y: auto;
                    gap: 0.5rem;
                `}
            >
                {addedOrders.map(({ order, items }) => (
                    <AddedOrderCard
                        key={order?.order_id || items[0]?.item_id}
                        order={order}
                        items={items}
                        addedItems={addedItems}
                        callbacks={callbacks}
                    />
                ))}
            </Grid>
            <Grid
                container
                justifyContent="center"
                css={css`
                    padding-top: 0.75rem;
                    gap: 0.75rem;
                `}
            >
                {state?.newManifest?.isDraft && (
                    <PrimaryButton onClick={onDeleteDraft} red>
                        Delete Manifest Draft
                    </PrimaryButton>
                )}
                <PrimaryButton disabled={createDisabled} onClick={onCreate}>
                    {isUpdate ? 'Update' : 'Create'}
                </PrimaryButton>
            </Grid>
        </Grid>
    );
};

export default ManifestColumn;
